export const MAX_AUDIO_BYTES = 2 * 1024 * 1024 * 1024;
export const isAudioFile = file => /\.(mp3|m4a|m4b)$/i.test(file.name);
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const cleanTitle = value => String(value || '').replace(/\.[^.]+$/, match => /\.(mp3|m4a|m4b)$/i.test(match) ? '' : match)
  .replace(/^\s*\d+[_.\s-]+(?=\S)/, '').replace(/_/g, ' ').trim();
const withoutPart = title => title.replace(/[\s,._-]+(?:part|pt\.?|часть|части)[\s._-]*\d+\s*$/i, '').trim();

// Pure suggestion logic: only explicit part names or chapter subfolders are
// grouped. Repeated album/book titles alone are not evidence of a chapter.
export function suggestChapters(items) {
  const rows = items.map(({ file, metadata, failed }, index) => {
    const common = metadata?.common || {};
    const embedded = metadata?.format?.chapters || [];
    const path = file.webkitRelativePath || file.name;
    const parts = path.split('/');
    const folder = parts.length > 2 ? parts.slice(0, -1).join('/') : '';
    const title = cleanTitle(embedded[0]?.title || common.title || file.name) || file.name;
    const base = withoutPart(title);
    const group = folder ? `folder:${folder}` : base !== title ? `part:${base.toLocaleLowerCase()}` : null;
    return { id: index, file, path, title: folder ? cleanTitle(parts[parts.length - 2]) : base,
      group, track: common.track?.no, disc: common.disk?.no || 1,
      duration: metadata?.format?.duration, album: common.album || '',
      embedded: embedded.map(c => c.title), keepEmbedded: true,
      useEmbeddedTitle: !embedded.length && /\.m4[ab]$/i.test(file.name), failed: !!failed };
  });
  const numbered = rows.every(r => Number.isInteger(r.track) && r.track > 0) &&
    new Set(rows.map(r => `${r.disc}/${r.track}`)).size === rows.length;
  rows.sort((a, b) => {
    if (a.path.includes('/') && b.path.includes('/') && a.group !== b.group) return collator.compare(a.path, b.path);
    return (numbered ? a.disc - b.disc || a.track - b.track : 0) || collator.compare(a.path, b.path);
  });
  return rows.map((row, i) => ({ ...row, startChapter: !i || !row.group || row.group !== rows[i - 1].group }));
}

export async function inspectAudioFiles(files, isCancelled = () => false) {
  const { parseBlob } = await import('music-metadata');
  const items = [];
  // Sequential reads bound memory and avoid hundreds of concurrent file reads.
  for (const file of files) {
    if (isCancelled()) return [];
    try { items.push({ file, metadata: await parseBlob(file, { skipCovers: true, includeChapters: true, duration: false }) }); }
    catch (_) { items.push({ file, failed: true }); }
  }
  return suggestChapters(items);
}

export const chapterPlan = rows => rows.map(r => ({ startChapter: r.startChapter, title: r.title.trim(), keepEmbedded: r.keepEmbedded, useEmbeddedTitle: !!r.useEmbeddedTitle }));
